import {
  alpha,
  Button,
  debounce,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Box } from "@mui/material";
import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import InitialsAvatar from "@/components/common/avatar/initialsAvatar";
import { ArrowLeftIcon, ArrowRightIcon } from "@/components/common/icons";
import { GREY, VIOLET } from "@/config/mui/colorPalette";
import { useClientBookingContext } from "@/contexts/clientBookingContext";

export default function ProviderScrollableCarousel({
  providers,
  selectedProvider,
  eligibleProviders,
  setSelectedProvider,
  customizationPrimaryColor,
}: {
  providers: ReturnType<typeof useClientBookingContext>["providers"];
  selectedProvider: ReturnType<
    typeof useClientBookingContext
  >["selectedProvider"];
  eligibleProviders: ReturnType<
    typeof useClientBookingContext
  >["eligibleProviders"];
  setSelectedProvider: ReturnType<
    typeof useClientBookingContext
  >["setSelectedProvider"];
  customizationPrimaryColor?: string;
  customizationTextColor?: string;
}) {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isOverflowed, setIsOverflowed] = useState(false);

  const updateScrollButtons = useCallback(() => {
    const container = containerRef.current;
    if (container) {
      // Check if content is overflowed
      const hasOverflow = container.scrollWidth > container.clientWidth;
      setIsOverflowed(hasOverflow);

      // Only update scroll buttons if there's overflow
      if (hasOverflow) {
        setCanScrollLeft(container.scrollLeft > 0);
        setCanScrollRight(
          container.scrollWidth - container.scrollLeft >
            container.clientWidth + 1
        );
      }
    }
  }, []);

  useLayoutEffect(() => {
    updateScrollButtons();
    const debouncedUpdateScrollButtons = debounce(updateScrollButtons, 100);

    // Add scroll event listener
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", updateScrollButtons);

      updateScrollButtons();
    }
    window.addEventListener("resize", debouncedUpdateScrollButtons);
    return () => {
      if (container) {
        container.removeEventListener("scroll", updateScrollButtons);
      }
      window.removeEventListener("resize", debouncedUpdateScrollButtons);
    };
  }, [updateScrollButtons]);

  const scroll = (direction: "left" | "right") => {
    const container = containerRef.current;
    if (container) {
      container.scrollBy({
        left: direction === "left" ? -250 : 250,
        behavior: "smooth",
      });
    }
  };

  const selectedProviderId = selectedProvider?.user.id;

  return (
    <Stack
      display="flex"
      sx={{
        gap: 2,
        mb: 3,
      }}
    >
      <Stack
        flexDirection="row"
        sx={{
          gap: 2,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h3">Select provider</Typography>
        <Box sx={{ height: "34px" }}>
          {isOverflowed && (
            <>
              <IconButton
                onClick={() => scroll("left")}
                disabled={!canScrollLeft}
                aria-label="Scroll providers list to the left"
                size="small"
              >
                <ArrowLeftIcon
                  color={canScrollLeft ? theme.palette.primary.main : GREY[40]}
                />
              </IconButton>
              <IconButton
                onClick={() => scroll("right")}
                disabled={!canScrollRight}
                aria-label="Scroll providers list to the right"
                size="small"
              >
                <ArrowRightIcon
                  color={canScrollRight ? theme.palette.primary.main : GREY[40]}
                />
              </IconButton>
            </>
          )}
        </Box>
      </Stack>
      <Box
        ref={containerRef}
        component="div"
        sx={{
          display: "flex",
          gap: 1.5,
          overflowX: "auto",
          scrollBehavior: "smooth",
          overflowY: "hidden",
          "&::-webkit-scrollbar": { display: "none" },
          pr: 3,
        }}
      >
        {eligibleProviders.map((elegibleProvider) => {
          const userIndex = providers.findIndex(
            (provider) => provider.user.id === elegibleProvider.user.id
          );
          return (
            <ProviderCard
              key={elegibleProvider.user.id}
              label={`${elegibleProvider.user.firstName} ${elegibleProvider.user.lastName}`}
              selected={elegibleProvider.user.id === selectedProviderId}
              cardAvatar={
                <InitialsAvatar
                  initials={{
                    firstName: elegibleProvider.user.firstName,
                    lastName: elegibleProvider.user.lastName,
                    userIndex,
                  }}
                  size={32}
                  sx={{ fontSize: "0.875rem" }}
                />
              }
              onSelectionChange={() => setSelectedProvider(elegibleProvider)}
              customizationPrimaryColor={customizationPrimaryColor}
            />
          );
        })}
      </Box>
    </Stack>
  );
}

const ProviderCard = ({
  label,
  cardAvatar,
  selected,
  onSelectionChange,
  customizationPrimaryColor,
}: {
  label: string;
  cardAvatar: React.ReactNode;
  selected?: boolean;
  onSelectionChange: () => void;
  customizationPrimaryColor?: string;
}) => {
  const theme = useTheme();
  const selectedBackgroundColor = customizationPrimaryColor
    ? alpha(customizationPrimaryColor, 0.04)
    : VIOLET[20];
  return (
    <Button
      sx={{
        display: "flex",
        py: 1,
        pr: 2,
        pl: 1.5,
        gap: 1,
        border: "1px solid",
        borderRadius: "8px",
        borderColor: GREY[20],
        whiteSpace: "nowrap",
        flexShrink: 0,
        alignItems: "center",
        ...(selected && {
          border: "1.5px solid",
          borderColor: theme.palette.primary.main,
          backgroundColor: selectedBackgroundColor,
        }),
        boxSizing: "border-box",
      }}
      onClick={onSelectionChange}
    >
      {cardAvatar}
      <Typography
        variant={selected ? "subtitleSmall" : "paragraphSmall"}
        sx={{
          color: "text.primary",
          minWidth: "fit-content",
          // Prevent shifting in layout when selected
          "&:after": {
            content: `"${label}"`,
            display: "block",
            height: "0px",
            pointerEvents: "none",
            visibility: "hidden",
            fontWeight: "subtitleSmall.fontWeight",
          },
        }}
      >
        {label}
      </Typography>
    </Button>
  );
};
